import React, { useMemo, useState, useEffect } from "react";
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Box,
    TablePagination, Tooltip, Typography,
    Dialog, DialogTitle, DialogContent, IconButton,
    Grid, TextField, InputAdornment, Button, Skeleton,
} from "@mui/material";
import * as XLSX from "xlsx";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SingleSelect from "../../components/Selections/singleSelect";
import MultiSelectWithAll from "../../components/Selections/multiSelectWithAll";
import { getClientTheme } from "../../theme/clientTheme";
import { useData } from "../../context/DataContext";

/* =================================================================
   ADOPTION PRACTICE CONFIG
   (kept in sync with AdOptionCreation -> AdOptionCreationFields)
   Drives the grouped columns of the summary table.
================================================================= */
const AdOptionCreationFields = [
    {
        fields: [
            { label: "Irrigation", name: "irrigation" },
            { label: "Total growing farmers", name: "total_growing_farmers" },
            { label: "Crop Rotation", name: "cropRotation" },
            { label: "Summer deep ploughing once in 2-3 Years", name: "summerDeepPloughing" },
            { label: "Inter crop/Cover crop", name: "InterCrop" },
            { label: "border and Trap crop", name: "borderandTrapcrop" },
            { label: "soil test based fertilizer application", name: "soilTestFertilizer" },
            { label: "Application fertilzers in 4R's method", name: "application_fertilzers_4R_method" },
            { label: "Selection of Quality seed", name: "selectionQualitySeed" },
            { label: "Navadanya Cultivation as green manure", name: "navadanyaCultivationasgreenmanure" },
            { label: "Waste Decomposer", name: "wasteDecomposer" },
            { label: "Avoid Top dressing of Complex fertilizers", name: "avoidTopdressingofComplexfertilizers" },
        ],
    },
    {
        titleData: "organic mannure application",
        fields: [
            { label: "Vermi compost", name: "vermiCompost" },
            { label: "FYM", name: "FYMValue" },
            { label: "Tank Silt", name: "tankSilt" },
            { label: "Sheep Penning", name: "sheep_penning" },
        ],
        footerData: [{ label: "foliar spray of nutrients", name: "foliarSprayofNutrients" }],
    },
    {
        titleData: "Botanical Extracts",
        fields: [
            { label: "Neem", name: "selectedNeemValue" },
            { label: "Vitex", name: "selectedVitexValue" },
        ],
    },
    {
        titleData: "Traps",
        fields: [
            { label: "pheromone", name: "pheromone" },
            { label: "cremit gel", name: "cremitgel" },
            { label: "light Traps", name: "lightTraps" },
            { label: "sticky cards", name: "sticky" },
        ],
    },
    {
        fields: [
            { label: "stem application", name: "stemApplication" },
            { label: "Regular Field bservations", name: "regularFieldbservations" },
            { label: "Avoid chemical spray up to 45 days", name: "avoid_chemicalsprayup45days" },
            { label: "ETL level based application", name: "eTLlevelBasedApplication" },
        ],
    },
    {
        titleData: "Irrigation Method",
        fields: [
            { label: "Alternate", name: "alternate" },
            { label: "Critical growth stage irrigation", name: "criticalGrowthStageIrrigation" },
            { label: "Drip", name: "selectedDrip" },
        ],
        footerData: [{ label: "Avoid Repitition of Same pesticides", name: "avoidRepititionofSamepesticides" }],
    },
    {
        titleData: "PPE",
        fields: [
            { label: "Full body covering dress", name: "fullbodyCoveringDress" },
            { label: "boots/shoes", name: "selectedDootsAndshoes" },
            { label: "Mask", name: "selectedMaks" },
            { label: "goggles/helmet", name: "selectedGogglesAndHelmet" },
            { label: "gloves", name: "selectedGloves" },
            { label: "Minimum PPE", name: "selectedFullPPE" },
        ],
        footerData: [
            { label: "Appropriate PPE", name: "appropriate_PPE" },
            { label: "Proper disposal of chemical containers", name: "properDisposalOfChemicalContainers" },
            { label: "Proper maintaine of pesticide application equipement", name: "applicationEquipement" },
            { label: "Using of Traichogramma Cards", name: "usingofTraichogrammaCards" },
        ],
    },
    {
        fields: [
            { label: "reduce intercultural operations", name: "reduceInterculturalOperationsAwarenessonthechildLabour" },
            { label: "Avoid the child labour", name: "avoidChildLabour" },
            { label: "Awareness and providing  Minimum wages", name: "minimumWages" },
            { label: "Maintainance of FFB's", name: "maintainanceOfFFBs" },
            { label: "plough across the slope", name: "ploughAcrossTheslope" },
            { label: "Using of Bio fertilizers", name: "biofertilizer" },
            { label: "Use of Nano Urea", name: "useOfNanoUrea" },
            { label: "Use of Neo/Nano Potash", name: "useOfNanoUreaPotash" },
            { label: "Biochar", name: "biochar" },
            { label: "Food Spray", name: "foodSpray" },
            { label: "removal of rosette flowers", name: "removalofrosetteflowers" },
            { label: "Avoid Contamination", name: "avoidContamination" },
            { label: "Avoid PolyPropylene Bags", name: "avoidPolyPropyleneBags" },
            { label: "Crop residue management", name: "cropresidueManagement" },
            { label: "Cotton Grading and Safe storage", name: "cottonGradingSafeStorage" },
        ],
    },
    {
        titleData: "Using of Bio pesticides",
        fields: [
            { label: "Trichoderma", name: "trichoderma" },
            { label: "Beaveria bassiana", name: "beaveriaBassiana" },
        ],
    },
    {
        titleData: "Usage of Organic pesticides",
        fields: [
            { label: "Agni astra", name: "agniAstra" },
            { label: "Neem astra", name: "neemAstra" },
            { label: "Dashaparni kashayam", name: "dashaparnikashayam" },
            { label: "Vermi wash", name: "vermiWash" },
        ],
        footerData: [
            { label: "Using of Cocktails by farmers", name: "usingOfCocktailsByFarmers" },
            { label: "Minimize the use of PAN group 3 Pesticides", name: "panGroupPesticides" },
        ],
    },
    {
        titleData: "Using of HHP's by farmers",
        fields: [
            { label: "Monocrotophos", name: "monocrotophos" },
            { label: "Lambda cyhalothrin", name: "lambdaCyhalothrin" },
            { label: "Carbendazim", name: "carbendazim" },
            { label: "Propiconazole", name: "propiconazole" },
            { label: "Bifenthrin", name: "bifenthrin" },
        ],
        footerData: [
            { label: "Knowledge on  colour codes of pesticides", name: "codeOfpesticides" },
            { label: "Received any training on pesticide spraying techniques", name: "pesticidesprayingTechniques" },
        ],
    },
];

/* =================================================================
   Build the header model from the config.
   headerGroups: ordered list of
     - { kind: "group",  title, leaves: [{ name, label }] }
     - { kind: "single", name, label }
   leafColumns: flat ordered list of every leaf { name, label }
================================================================= */
const buildColumnModel = () => {
    const headerGroups = [];

    AdOptionCreationFields.forEach((section) => {
        const fieldLeaves = (section.fields || []).map((f) => ({ name: f.name, label: f.label }));

        if (section.titleData) {
            headerGroups.push({ kind: "group", title: section.titleData, leaves: fieldLeaves });
        } else {
            fieldLeaves.forEach((leaf) => headerGroups.push({ kind: "single", ...leaf }));
        }

        if (section.footerData) {
            section.footerData.forEach((f) =>
                headerGroups.push({ kind: "single", name: f.name, label: f.label })
            );
        }
    });

    const leafColumns = headerGroups.flatMap((g) =>
        g.kind === "group" ? g.leaves : [{ name: g.name, label: g.label }]
    );

    return { headerGroups, leafColumns };
};

const { headerGroups, leafColumns } = buildColumnModel();

const AdOptionSummaryReports = ({ yearId, levelId }) => {
    const {
        adOptionData = [],
        adOptionPagination = null,
        user_client_id,
        user = {},
        adOptionPage = 1,
        adOptionLimit = 10,
        adOptionLoading = false,
        handleAdOptionPageChange,
        handleAdOptionLimitChange,
    } = useData();
    const clientTheme = getClientTheme(user_client_id);

    /* ================= GROUP BY VILLAGE OR GROUP =================
       level === 1 => Village, else Group (matches Dashboard convention). */
    const levelOptions = [
        { value: 1, label: "Villages" },
        { value: 2, label: "Groups" },
    ];

    // higher-level users are locked to their own level; level 1/2 users can switch
    const isTopUser = user.user_level_id === 1 || user.user_level_id === 2;
    const initialLevel = isTopUser ? levelId || 1 : user.level_id || 1;

    const [selectedLevel, setSelectedLevel] = useState(initialLevel);

    // keep in sync with the Dashboard's level filter
    useEffect(() => {
        if (isTopUser && levelId) setSelectedLevel(levelId);
    }, [levelId, isTopUser]);

    const groupByVillage = selectedLevel === 1;
    const dimKey = groupByVillage ? "village_name" : "group_name";
    const dimHeader = groupByVillage ? "Village Name" : "Group Name";

    /* ================= NAME FILTER (village / group) — multi select ================= */
    const [selectedNames, setSelectedNames] = useState([]);
    const [search, setSearch] = useState("");

    /* ================= FILTER BY YEAR (optional) ================= */
    const yearFilteredData = useMemo(() => {
        if (!yearId) return adOptionData;
        return adOptionData.filter((item) => +item.crop_year_id === +yearId);
    }, [adOptionData, yearId]);

    /* ================= AGGREGATE BY VILLAGE / GROUP ================= */
    const villageSummary = useMemo(() => {
        const map = new Map();

        yearFilteredData.forEach((record) => {
            const village = record[dimKey] || "-";
            const report = record.adoption_report?.[0] || {};

            if (!map.has(village)) {
                map.set(village, {
                    village,
                    totalFarmers: 0,
                    counts: leafColumns.reduce((acc, c) => ({ ...acc, [c.name]: 0 }), {}),
                });
            }

            const entry = map.get(village);
            entry.totalFarmers += 1;

            leafColumns.forEach(({ name }) => {
                if (report[name]?.value === 1) {
                    entry.counts[name] += 1;
                }
            });
        });

        return Array.from(map.values()).sort((a, b) => a.village.localeCompare(b.village));
    }, [yearFilteredData, dimKey]);

    /* ================= NAME OPTIONS (from aggregated rows) ================= */
    const nameOptions = useMemo(
        () => villageSummary.map((r) => ({ value: r.village, label: r.village })),
        [villageSummary]
    );

    // default: all villages/groups selected (re-applied when level/year/data change)
    useEffect(() => {
        setSelectedNames(nameOptions);
    }, [nameOptions]);

    /* ================= FILTER (selected names + search) ================= */
    const filteredSummary = useMemo(() => {
        let rows = villageSummary;

        if (selectedNames.length) {
            const picked = new Set(selectedNames.map((n) => n.value));
            rows = rows.filter((r) => picked.has(r.village));
        }

        const q = search.trim().toLowerCase();
        if (q) rows = rows.filter((r) => r.village.toLowerCase().includes(q));

        return rows;
    }, [villageSummary, selectedNames, search]);

    /* ================= GRAND TOTAL ROW ================= */
    const grandTotal = useMemo(() => {
        return filteredSummary.reduce(
            (acc, row) => {
                acc.totalFarmers += row.totalFarmers;
                leafColumns.forEach(({ name }) => {
                    acc.counts[name] += row.counts[name];
                });
                return acc;
            },
            {
                totalFarmers: 0,
                counts: leafColumns.reduce((a, c) => ({ ...a, [c.name]: 0 }), {}),
            }
        );
    }, [filteredSummary]);

    /* ================= PAGINATION (server-side, via context) ================= */
    // page is 1-based in context; TablePagination is 0-based
    const page = (adOptionPage || 1) - 1;
    const rowsPerPage = adOptionLimit || 10;

    const handleChangePage = (event, newPage) => handleAdOptionPageChange?.(newPage + 1);
    const handleChangeRowsPerPage = (event) =>
        handleAdOptionLimitChange?.(parseInt(event.target.value, 10));

    // real total from the API pagination metadata (fallback keeps Prev/Next usable)
    const rowCount =
        adOptionPagination?.totalRecords ??
        (adOptionData.length < rowsPerPage
            ? page * rowsPerPage + adOptionData.length
            : (page + 1) * rowsPerPage + 1);

    // server-side: render the current page's aggregated rows as-is (no client slice)
    const displayedRows = filteredSummary;

    /* ================= FARMER DRILL-DOWN MODAL ================= */
    const [modal, setModal] = useState({ open: false, title: "", rows: [] });

    const openFarmers = ({ village, leaf }) => {
        let rows = yearFilteredData;

        if (village) {
            rows = rows.filter((r) => (r[dimKey] || "-") === village);
        }
        if (leaf) {
            rows = rows.filter((r) => r.adoption_report?.[0]?.[leaf.name]?.value === 1);
        }

        const scope = village ? ` — ${village}` : groupByVillage ? " — All Villages" : " — All Groups";
        const title = leaf ? `${leaf.label}${scope}` : `Total Farmers${scope}`;

        setModal({ open: true, title, rows });
    };

    const closeFarmers = () => setModal((prev) => ({ ...prev, open: false }));

    /* ================= EXCEL DOWNLOAD (same grouped tabular format) ================= */
    const handleDownloadExcel = () => {
        const totalCols = 3 + leafColumns.length; // S.No + dim + Total Farmers + leaves

        // two header rows + merges to reproduce the grouped header
        const row1 = new Array(totalCols).fill("");
        const row2 = new Array(totalCols).fill("");
        const merges = [];

        // fixed columns span both header rows
        ["S.No", dimHeader, "Total Farmers"].forEach((label, c) => {
            row1[c] = label;
            merges.push({ s: { r: 0, c }, e: { r: 1, c } });
        });

        let col = 3;
        headerGroups.forEach((g) => {
            if (g.kind === "group") {
                row1[col] = g.title;
                merges.push({ s: { r: 0, c: col }, e: { r: 0, c: col + g.leaves.length - 1 } });
                g.leaves.forEach((leaf, j) => {
                    row2[col + j] = leaf.label;
                });
                col += g.leaves.length;
            } else {
                row1[col] = g.label;
                merges.push({ s: { r: 0, c: col }, e: { r: 1, c: col } });
                col += 1;
            }
        });

        // data rows (current filter set) + grand total
        const dataRows = filteredSummary.map((row, idx) => [
            idx + 1,
            row.village,
            row.totalFarmers,
            ...leafColumns.map((c) => row.counts[c.name]),
        ]);

        const totalRow = [
            "Total",
            "",
            grandTotal.totalFarmers,
            ...leafColumns.map((c) => grandTotal.counts[c.name]),
        ];

        const aoa = [row1, row2, ...dataRows, totalRow];

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws["!merges"] = merges;
        // merge the "Total" label across S.No + dimension columns
        ws["!merges"].push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: 1 } });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Adoption Summary");
        const scope = groupByVillage ? "Village" : "Group";
        XLSX.writeFile(wb, `${scope}_Wise_Adoption_Summary.xlsx`);
    };

    /* ================= STYLES ================= */
    const headerStyle = {
        border: "1px solid #fff",
        fontWeight: 700,
        backgroundColor: clientTheme.activeBg,
        color: "#FFF",
        fontSize: "12px",
        whiteSpace: "nowrap",
    };

    const groupHeaderStyle = {
        ...headerStyle,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
    };

    const bodyStyle = {
        border: "1px solid #e0e0e0",
        fontSize: "13px",
    };

    const totalStyle = {
        ...bodyStyle,
        fontWeight: 700,
        backgroundColor: "#f1f8f4",
    };

    // clickable count style (applied only when count > 0)
    const clickableSx = {
        cursor: "pointer",
        color: "#1976d2",
        fontWeight: 600,
        "&:hover": { textDecoration: "underline" },
    };

    // renders a count cell; clickable + drill-down only when value > 0
    const renderCount = (key, value, onClick, baseSx) => (
        <TableCell
            key={key}
            align="center"
            sx={{ ...baseSx, ...(value > 0 ? clickableSx : {}) }}
            onClick={value > 0 ? onClick : undefined}
        >
            {value}
        </TableCell>
    );

    const stickyLeftHeader = { position: "sticky", left: 0, zIndex: 4 };
    const stickyLeftBody = { position: "sticky", left: 0, zIndex: 1, backgroundColor: "#fff" };

    /* ================= EMPTY STATE ================= */
    if (villageSummary.length === 0) {
        return (
            <Box
                sx={{
                    minHeight: 160,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                }}
            >
                <BarChartOutlinedIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                    No Adoption Data Found
                </Typography>
                <Typography variant="caption">Nothing to summarize yet</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            {/* ================= TOOLBAR: LEVEL + NAMES + SEARCH ================= */}
            <Grid container spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                {/* Level dropdown: only for user_level_id 1 or 2 */}
                {isTopUser && (
                    <Grid item size={{ xs: 12, md: 3 }}>
                        <SingleSelect
                            name="selectedLevel"
                            label="Level"
                            options={levelOptions}
                            value={selectedLevel}
                            onChange={(_, value) => {
                                setSelectedLevel(+value || 1);
                            }}
                        />
                    </Grid>
                )}

                <Grid item size={{ xs: 12, md: isTopUser ? 4 : 5 }}>
                    <MultiSelectWithAll
                        name="selectedNames"
                        label={groupByVillage ? "Select Villages" : "Select Groups"}
                        options={nameOptions}
                        value={selectedNames}
                        onChange={(_, values) => {
                            setSelectedNames(values || []);
                        }}
                    />
                </Grid>

                {/* Search bar on the right */}
                <Grid item size={{ xs: 12, md: isTopUser ? 3 : 4 }} sx={{ ml: "auto" }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={`Search ${groupByVillage ? "village" : "group"}...`}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {/* Excel download */}
                <Grid item size={{ xs: 12, md: 2 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        startIcon={<FileDownloadOutlinedIcon />}
                        onClick={handleDownloadExcel}
                        disabled={filteredSummary.length === 0}
                    >
                        Excel
                    </Button>
                </Grid>
            </Grid>

            <TableContainer
                component={Paper}
                sx={{
                    overflowX: "auto",
                    borderRadius: 3,
                    boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                    sx={{ "& .MuiTableCell-root": { padding: "8px" } }}
                >
                    <TableHead>
                        {/* ---------- Row 1: fixed cols + group titles / standalone ---------- */}
                        <TableRow>
                            <TableCell rowSpan={2} align="center" sx={{ ...headerStyle }}>
                                S.No
                            </TableCell>
                            <TableCell
                                rowSpan={2}
                                align="center"
                                sx={{ ...headerStyle, ...stickyLeftHeader }}
                            >
                                {dimHeader}
                            </TableCell>
                            <TableCell rowSpan={2} align="center" sx={headerStyle}>
                                Total Farmers
                            </TableCell>

                            {headerGroups.map((g, i) =>
                                g.kind === "group" ? (
                                    <TableCell
                                        key={`g-${i}`}
                                        colSpan={g.leaves.length}
                                        align="center"
                                        sx={groupHeaderStyle}
                                    >
                                        {g.title}
                                    </TableCell>
                                ) : (
                                    <TableCell
                                        key={`s-${i}`}
                                        rowSpan={2}
                                        align="center"
                                        sx={headerStyle}
                                    >
                                        <Tooltip title={g.label}>
                                            <span>{g.label}</span>
                                        </Tooltip>
                                    </TableCell>
                                )
                            )}
                        </TableRow>

                        {/* ---------- Row 2: leaves of grouped columns ---------- */}
                        <TableRow>
                            {headerGroups.map((g, i) =>
                                g.kind === "group"
                                    ? g.leaves.map((leaf, j) => (
                                        <TableCell
                                            key={`gl-${i}-${j}`}
                                            align="center"
                                            sx={headerStyle}
                                        >
                                            <Tooltip title={leaf.label}>
                                                <span>{leaf.label}</span>
                                            </Tooltip>
                                        </TableCell>
                                    ))
                                    : null
                            )}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {/* Shimmer loader while a page is being fetched */}
                        {adOptionLoading &&
                            Array.from({ length: rowsPerPage }).map((_, r) => (
                                <TableRow key={`sk-${r}`}>
                                    {Array.from({ length: 3 + leafColumns.length }).map((__, c) => (
                                        <TableCell key={`sk-${r}-${c}`} align="center" sx={bodyStyle}>
                                            <Skeleton variant="text" animation="wave" sx={{ mx: "auto" }} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}

                        {!adOptionLoading && displayedRows.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={3 + leafColumns.length}
                                    align="center"
                                    sx={{ ...bodyStyle, py: 3, color: "text.secondary" }}
                                >
                                    No matching {groupByVillage ? "villages" : "groups"} found
                                </TableCell>
                            </TableRow>
                        )}
                        {!adOptionLoading && displayedRows.map((row, idx) => (
                            <TableRow
                                key={row.village}
                                hover
                                sx={{ "&:nth-of-type(even)": { backgroundColor: "#fafafa" } }}
                            >
                                <TableCell align="center" sx={bodyStyle}>
                                    {page * rowsPerPage + idx + 1}
                                </TableCell>
                                <TableCell
                                    align="left"
                                    sx={{ ...bodyStyle, fontWeight: 600, ...stickyLeftBody }}
                                >
                                    {row.village}
                                </TableCell>
                                {renderCount(
                                    "tf",
                                    row.totalFarmers,
                                    () => openFarmers({ village: row.village, leaf: null }),
                                    bodyStyle
                                )}
                                {leafColumns.map((c) =>
                                    renderCount(
                                        c.name,
                                        row.counts[c.name],
                                        () => openFarmers({ village: row.village, leaf: c }),
                                        bodyStyle
                                    )
                                )}
                            </TableRow>
                        ))}

                        {/* ---------- Grand Total ---------- */}
                        {!adOptionLoading && displayedRows.length > 0 && (
                            <TableRow>
                                <TableCell align="center" sx={totalStyle} colSpan={2}>
                                    Total
                                </TableCell>
                                {renderCount(
                                    "tf-total",
                                    grandTotal.totalFarmers,
                                    () => openFarmers({ village: null, leaf: null }),
                                    totalStyle
                                )}
                                {leafColumns.map((c) =>
                                    renderCount(
                                        c.name,
                                        grandTotal.counts[c.name],
                                        () => openFarmers({ village: null, leaf: c }),
                                        totalStyle
                                    )
                                )}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={rowCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
            />

            {/* ================= FARMER DETAILS MODAL ================= */}
            <Dialog open={modal.open} onClose={closeFarmers} maxWidth="md" fullWidth>
                <DialogTitle
                    sx={{
                        backgroundColor: clientTheme.activeBg,
                        color: "#FFF",
                        pr: 6,
                        fontSize: "15px",
                        fontWeight: 700,
                    }}
                >
                    {modal.title}
                    <Typography variant="caption" sx={{ display: "block", opacity: 0.85 }}>
                        {modal.rows.length} farmer{modal.rows.length === 1 ? "" : "s"}
                    </Typography>
                    <IconButton
                        onClick={closeFarmers}
                        sx={{ position: "absolute", right: 8, top: 8, color: "#FFF" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {modal.rows.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                            No farmers found
                        </Box>
                    ) : (
                        <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {["S.No", "Farmer Name", dimHeader, "Mobile", "Gender", "Year"].map((h) => (
                                            <TableCell key={h} sx={headerStyle}>
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {modal.rows.map((f, i) => (
                                        <TableRow
                                            key={f.id ?? i}
                                            hover
                                            sx={{ "&:nth-of-type(even)": { backgroundColor: "#fafafa" } }}
                                        >
                                            <TableCell sx={bodyStyle}>{i + 1}</TableCell>
                                            <TableCell sx={bodyStyle}>{f.farmer_name || "-"}</TableCell>
                                            <TableCell sx={bodyStyle}>{f[dimKey] || "-"}</TableCell>
                                            <TableCell sx={bodyStyle}>{f.farmer_mobile || "-"}</TableCell>
                                            <TableCell sx={bodyStyle}>{f.gender || "-"}</TableCell>
                                            <TableCell sx={bodyStyle}>{f.year || "-"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default AdOptionSummaryReports;
